import { useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { X, ShoppingCart, Trash2, Plus, Minus, Loader2, Pencil } from "lucide-react";
import axios from "axios";
import { useCart } from "../context/CartContext";
import { API } from "../context/AuthContext";
import { useCartPreview, CartPriceLines } from "./CartPricePreview";

/**
 * Right-side slide-out cart overlay. Whole-shop cart grouped by event.
 * Logged-in users also see their final prices (same /checkout/preview as checkout).
 */
export const UnavailableAlert = ({ reason, testid, onRemove }) => (reason ? (
  <div className="mt-1 flex items-center gap-2 text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-2 py-1" role="alert" data-testid={testid}>
    <span className="flex-1">{reason}</span>
    {onRemove && <button onClick={onRemove} className="underline font-bold" data-testid={`${testid}-remove`}>Remove</button>}
  </div>
) : null);

export default function CartOverlay() {
  const { isOpen, close, groupedByEvent, subtotal, count, setTicketQty, setPackageQty, removeItem, removePackage, unavailable = {} } = useCart();
  const unavailableCount = Object.keys(unavailable).length;
  const { preview, loading: previewLoading, loggedIn } = useCartPreview(isOpen);
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const location = useLocation();

  // iter173: when a shopper hits `+` on a package that requires variants,
  // we open an inline picker for the new attendee instead of silently
  // adding a variant-less copy (which would render "Attendee #N: —" in the
  // cart). Modal is package-scoped so the cart can service multiple
  // packages in one visit.
  // iter186: modal also supports EDITING an already-added attendee. When
  // `addingFor.editIndex` is a number, save REPLACES the copy at that
  // index instead of appending a new one.
  const [addingFor, setAddingFor] = useState(null); // { pkg, tiers, editIndex } | null
  const [addingLoading, setAddingLoading] = useState(false);
  const [newCopy, setNewCopy] = useState([]); // [{tier_id, variant_id, variant_name}]
  const [addError, setAddError] = useState(null);

  // Best-effort tenant slug: pull from URL path when useParams misses (icon lives outside <Routes>).
  const slug = tenantSlug || (location.pathname.split("/")[1] || "mymaitree");

  // Hide overlay on non-shopping surfaces (mirrors GlobalCartButton).
  const path = location.pathname || "";
  if (path.startsWith("/admin")) return null;
  if (/\/[^/]+\/admin(\/|$)/.test(path)) return null;
  if (/\/[^/]+\/expense(\/|$)/.test(path)) return null;

  const startCheckout = () => {
    if (count === 0) return;
    close();
    navigate(`/${slug}/checkout`);
  };

  // iter173: fetch tier metadata for a package on demand (cart entries don't
  // carry it) and either open the picker OR commit the increment directly
  // when the package has zero variant-bearing tiers.
  const _defaultCopyFor = (tiers) => tiers.map(t => {
    const vs = t.variants || [];
    return {
      tier_id: t.id,
      variant_id: vs.length > 0 ? vs[0].id : null,
      variant_name: vs.length > 0 ? vs[0].name : null,
    };
  });
  const onPlusPackage = async (pkg) => {
    setAddError(null);
    setAddingLoading(true);
    try {
      const r = await axios.get(`${API}/ticketing/public/events/${pkg.event_id}`, {
        headers: { "X-Tenant-Slug": slug },
      });
      const packages = r.data?.packages || [];
      const found = packages.find(p => p.id === pkg.package_id);
      const tiers = found?.tiers || [];
      const hasVariants = tiers.some(t => (t.variants || []).length > 0);
      if (!hasVariants) {
        // No variants to pick — just increment. Still trim/pad arrays.
        setPackageQty(pkg, pkg.quantity + 1);
        return;
      }
      setNewCopy(_defaultCopyFor(tiers));
      setAddingFor({ pkg, tiers, editIndex: null });
    } catch (e) {
      // Failed to fetch tier metadata — fall back to the previous
      // (variant-less) increment so the user isn't blocked.
      setPackageQty(pkg, pkg.quantity + 1);
      setAddError(e?.response?.data?.detail || "Could not load variants — added without options.");
    } finally {
      setAddingLoading(false);
    }
  };
  // iter186: pencil-icon click on any attendee line → open the same modal,
  // pre-filled with that copy's saved selection, and mark it edit-mode so
  // save replaces (rather than appends).
  const onEditAttendee = async (pkg, editIndex) => {
    setAddError(null);
    setAddingLoading(true);
    try {
      const r = await axios.get(`${API}/ticketing/public/events/${pkg.event_id}`, {
        headers: { "X-Tenant-Slug": slug },
      });
      const packages = r.data?.packages || [];
      const found = packages.find(p => p.id === pkg.package_id);
      const tiers = found?.tiers || [];
      const prevSel = Array.isArray(pkg.variant_selections) ? (pkg.variant_selections[editIndex] || []) : [];
      const rehydrated = tiers.map((t, occIdx) => {
        const sel = prevSel[occIdx] || {};
        const variants = t.variants || [];
        const v = variants.find(x => x.id === sel.variant_id) || variants[0];
        return {
          tier_id: t.id,
          variant_id: v?.id ?? null,
          variant_name: v?.name ?? null,
        };
      });
      setNewCopy(rehydrated);
      setAddingFor({ pkg, tiers, editIndex });
    } catch (e) {
      setAddError(e?.response?.data?.detail || "Could not load variants for editing.");
    } finally {
      setAddingLoading(false);
    }
  };
  const commitNewAttendee = () => {
    if (!addingFor) return;
    const { pkg, editIndex } = addingFor;
    const prevSel = Array.isArray(pkg.variant_selections) ? pkg.variant_selections : [];
    const prevNames = Array.isArray(pkg.variant_names) ? pkg.variant_names : [];
    const newNames = newCopy.map(s => s.variant_name).filter(Boolean);
    if (editIndex !== null && editIndex !== undefined) {
      // Edit mode — replace copy at editIndex, quantity unchanged.
      const nextSel = prevSel.map((c, i) => i === editIndex ? newCopy : c);
      const nextNames = prevNames.map((c, i) => i === editIndex ? newNames : c);
      setPackageQty({
        ...pkg,
        variant_selections: nextSel,
        variant_names: nextNames,
      }, pkg.quantity);
    } else {
      const nextSel = [...prevSel, newCopy];
      const nextNames = [...prevNames, newNames];
      setPackageQty({
        ...pkg,
        variant_selections: nextSel,
        variant_names: nextNames,
      }, pkg.quantity + 1);
    }
    setAddingFor(null);
    setNewCopy([]);
  };
  const cancelAddAttendee = () => { setAddingFor(null); setNewCopy([]); };
  const setNewCopyVariant = (occIdx, variantId) => {
    const tier = addingFor.tiers[occIdx];
    const v = (tier?.variants || []).find(x => x.id === variantId);
    setNewCopy(prev => prev.map((sel, i) => i === occIdx
      ? { ...sel, variant_id: variantId, variant_name: v?.name || null }
      : sel));
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
          onClick={close}
          data-testid="cart-overlay-backdrop"
        />
      )}
      <aside
        className={`fixed top-0 right-0 h-full w-full sm:w-[420px] bg-white z-50 flex flex-col transform transition-[transform,visibility] duration-300 ease-out ${isOpen ? "translate-x-0 shadow-2xl visible" : "translate-x-full invisible"}`}
        data-testid="cart-overlay"
        aria-hidden={!isOpen}
      >
        <header className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[#1e3a5f]" />
            <h2 className="font-extrabold text-[#1e3a5f] text-lg">Your cart</h2>
            {count > 0 && <span className="text-xs bg-[#1e3a5f] text-white rounded-full px-2 py-0.5 font-bold">{count}</span>}
          </div>
          <button onClick={close} className="p-2 hover:bg-slate-100 rounded-full" data-testid="cart-close-btn" aria-label="Close cart">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {groupedByEvent.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center py-16" data-testid="cart-empty-state">
              <ShoppingCart className="w-12 h-12 text-slate-300 mb-3" />
              <p className="text-slate-500 font-medium">Your cart is empty</p>
              <p className="text-slate-400 text-xs mt-1">Browse the shop and add tickets to get started.</p>
              <button
                onClick={() => { close(); navigate(`/${slug}`); }}
                className="mt-6 px-5 py-2 rounded-full bg-[#1e3a5f] text-white text-sm font-medium"
                data-testid="cart-continue-shopping-btn"
              >
                Continue shopping
              </button>
            </div>
          )}

          {groupedByEvent.map(g => (
            <div key={g.event_id} className="mb-5" data-testid={`cart-group-${g.event_id}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">{g.event_name}</p>
                <button
                  onClick={() => { close(); navigate(`/${slug}/event/${g.event_id}`); }}
                  className="text-xs text-[#1e3a5f] hover:underline"
                  data-testid={`cart-view-event-${g.event_id}`}
                >
                  View event
                </button>
              </div>
              <div className="space-y-2">
                {g.items.map(item => (
                  <div key={item.id} className="bg-slate-50 rounded-xl p-3 flex items-start gap-3" data-testid={`cart-item-${item.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1e3a5f] truncate">
                        {item.ticket_type_name}
                        {item.variant_name && <span className="text-slate-500 font-normal"> · {item.variant_name}</span>}
                      </p>
                      <p className="text-xs text-slate-500 mt-0.5">Base €{Number(item.base_unit_price || 0).toFixed(2)} each</p>
                      <UnavailableAlert reason={unavailable[item.id]} testid={`cart-item-unavailable-${item.id}`} onRemove={() => removeItem(item.id)} />
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => setTicketQty(item, item.quantity - 1)}
                          className="w-7 h-7 rounded-full bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center"
                          data-testid={`cart-item-minus-${item.id}`}
                          aria-label="Decrease"
                        ><Minus className="w-3 h-3" /></button>
                        <span className="w-6 text-center text-sm font-bold" data-testid={`cart-item-qty-${item.id}`}>{item.quantity}</span>
                        <button
                          onClick={() => setTicketQty(item, item.quantity + 1)}
                          className="w-7 h-7 rounded-full bg-[#1e3a5f] text-white hover:bg-[#15294a] flex items-center justify-center"
                          data-testid={`cart-item-plus-${item.id}`}
                          aria-label="Increase"
                        ><Plus className="w-3 h-3" /></button>
                        <span className={`ml-auto text-sm font-bold ${preview ? "text-slate-400 font-medium" : "text-[#1e3a5f]"}`}>
                          €{(Number(item.base_unit_price || 0) * item.quantity).toFixed(2)}
                        </span>
                        <button
                          onClick={() => removeItem(item.id)}
                          className="p-1 hover:bg-slate-200 rounded"
                          data-testid={`cart-item-remove-${item.id}`}
                          aria-label="Remove"
                        ><Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" /></button>
                      </div>
                    </div>
                  </div>
                ))}
                {g.packages.map(pkg => (
                  <div key={pkg.id} className="bg-cyan-50 rounded-xl p-3 flex items-start gap-3" data-testid={`cart-pkg-${pkg.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#0ea5e9] truncate">📦 {pkg.package_name}</p>
                      <UnavailableAlert reason={unavailable[pkg.id]} testid={`cart-pkg-unavailable-${pkg.id}`} onRemove={() => removePackage(pkg.id)} />
                      {Array.isArray(pkg.variant_names) && pkg.variant_names.length > 0 && (
                        <div className="mt-0.5 space-y-0.5" data-testid={`cart-pkg-variants-${pkg.id}`}>
                          {pkg.variant_names.map((copyNames, ci) => (
                            <div key={ci} className="flex items-center gap-1 text-[10px] text-slate-500" data-testid={`cart-pkg-${pkg.id}-copy-${ci}`}>
                              <span className="truncate flex-1 min-w-0">
                                Attendee #{ci + 1}: {(copyNames || []).filter(Boolean).join(" · ") || "—"}
                              </span>
                              <button
                                onClick={() => onEditAttendee(pkg, ci)}
                                disabled={addingLoading}
                                className="p-0.5 rounded hover:bg-white text-slate-400 hover:text-[#0ea5e9] shrink-0"
                                aria-label={`Edit attendee ${ci + 1}`}
                                data-testid={`cart-pkg-${pkg.id}-copy-${ci}-edit`}
                              >
                                <Pencil className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      <p className="text-xs text-slate-500 mt-0.5">Base €{Number(pkg.base_unit_price || 0).toFixed(2)} each</p>
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() => setPackageQty(pkg, pkg.quantity - 1)}
                          className="w-7 h-7 rounded-full bg-white border border-slate-200 hover:bg-slate-100 flex items-center justify-center"
                          data-testid={`cart-pkg-minus-${pkg.id}`}
                        ><Minus className="w-3 h-3" /></button>
                        <span className="w-6 text-center text-sm font-bold" data-testid={`cart-pkg-qty-${pkg.id}`}>{pkg.quantity}</span>
                        <button
                          onClick={() => onPlusPackage(pkg)}
                          disabled={addingLoading}
                          className="w-7 h-7 rounded-full bg-[#0ea5e9] text-white hover:bg-[#0284c7] disabled:opacity-50 flex items-center justify-center"
                          data-testid={`cart-pkg-plus-${pkg.id}`}
                        >{addingLoading && addingFor?.pkg?.id === pkg.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}</button>
                        <span className={`ml-auto text-sm font-bold ${preview ? "text-slate-400 font-medium" : "text-[#0ea5e9]"}`}>
                          €{(Number(pkg.base_unit_price || 0) * pkg.quantity).toFixed(2)}
                        </span>
                        <button
                          onClick={() => removePackage(pkg.id)}
                          className="p-1 hover:bg-slate-200 rounded"
                          data-testid={`cart-pkg-remove-${pkg.id}`}
                        ><Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {count > 0 && <CartPriceLines preview={preview} loading={previewLoading} />}
        </div>

        {count > 0 && (
          <footer className="border-t border-slate-200 px-5 py-4 bg-white">
            {unavailableCount > 0 && (
              <p className="mb-2 text-xs bg-red-50 text-red-700 rounded-lg px-3 py-2" data-testid="cart-unavailable-banner">
                {unavailableCount} item(s) in your cart are no longer available. Remove them to continue.
              </p>
            )}
            {preview ? (
              <>
                {preview.total < subtotal - 0.005 && (
                  <div className="flex items-baseline justify-between text-xs text-slate-400">
                    <span>Base subtotal</span><span className="line-through" data-testid="cart-subtotal">€{subtotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-sm text-slate-500">Your total</span>
                  <span className="text-xl font-extrabold text-[#2d8a4e] flex items-center gap-1.5" data-testid="cart-final-total">
                    {previewLoading && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" data-testid="cart-final-total-loading" />}
                    €{Number(preview.total).toFixed(2)}
                  </span>
                </div>
                <p className="text-[10px] text-slate-400 mb-3">Final prices for your login. Vouchers and wallet are applied at checkout.</p>
              </>
            ) : (
              <>
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-sm text-slate-500">Subtotal</span>
                  <span className="text-xl font-extrabold text-[#1e3a5f]" data-testid="cart-subtotal">€{subtotal.toFixed(2)}</span>
                </div>
                <p className="text-[10px] text-slate-400 mb-3" data-testid="cart-price-hint">
                  {loggedIn && previewLoading ? "Calculating your prices…" : "Base prices. Log in to see your final discounted prices — they are applied at checkout."}
                </p>
              </>
            )}
            <button
              onClick={startCheckout}
              disabled={unavailableCount > 0}
              className="w-full py-3 rounded-full bg-[#2d8a4e] text-white font-bold shadow disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="cart-checkout-btn"
            >
              Continue to checkout
            </button>
          </footer>
        )}
      </aside>

      {/* iter173: inline modal to pick variants for a new package
          attendee added straight from the cart. Sits above the cart
          overlay (z-60) so the shopper can still see their existing
          selections while choosing. */}
      {addingFor && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-3" data-testid="cart-add-attendee-modal">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-extrabold text-[#1e3a5f]">
                {addingFor.editIndex !== null && addingFor.editIndex !== undefined ? "Edit attendee" : "Add attendee to package"}
              </h3>
              <button onClick={cancelAddAttendee} className="p-1 hover:bg-slate-100 rounded-full" aria-label="Close" data-testid="cart-add-attendee-close">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <p className="text-sm font-semibold text-[#0ea5e9] mb-3">{addingFor.pkg.package_name}</p>
            <div className="overflow-y-auto -mx-1 px-1 space-y-2 flex-1">
              <p className="text-[11px] font-semibold text-slate-500">
                Attendee #{addingFor.editIndex !== null && addingFor.editIndex !== undefined
                  ? addingFor.editIndex + 1
                  : (addingFor.pkg.quantity || 0) + 1}
              </p>
              {addingFor.tiers.map((t, occIdx) => {
                const variants = t.variants || [];
                if (variants.length === 0) {
                  return (
                    <div key={occIdx} className="flex items-center justify-between text-xs text-slate-500 px-1">
                      <span className="truncate">{t.name}</span>
                      <span>€{Number(t.effective_price || t.price || 0).toFixed(2)}</span>
                    </div>
                  );
                }
                return (
                  <div key={occIdx} className="flex items-center justify-between gap-2 py-1">
                    <label className="text-xs text-slate-600 truncate flex-1 min-w-0" htmlFor={`cart-add-tier-${occIdx}`}>{t.name}</label>
                    <select
                      id={`cart-add-tier-${occIdx}`}
                      className="text-xs bg-white border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#0ea5e9]"
                      value={newCopy[occIdx]?.variant_id || variants[0].id}
                      onChange={(e) => setNewCopyVariant(occIdx, e.target.value)}
                      data-testid={`cart-add-tier-${occIdx}-variant-select`}
                    >
                      {variants.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-2 mt-4">
              <button onClick={cancelAddAttendee} className="flex-1 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50" data-testid="cart-add-attendee-cancel">
                Cancel
              </button>
              <button onClick={commitNewAttendee} className="flex-1 py-2 rounded-lg bg-[#0ea5e9] text-white text-sm font-bold hover:bg-[#0284c7]" data-testid="cart-add-attendee-save">
                {addingFor.editIndex !== null && addingFor.editIndex !== undefined ? "Save changes" : "Add attendee"}
              </button>
            </div>
            {addError && <p className="text-[11px] text-red-500 mt-2">{addError}</p>}
          </div>
        </div>
      )}
    </>
  );
}
